/**
 * OpenAI Ads Measurement Pixel for AIRJO.
 *
 * Tracks only confirmed browser-side Shopify storefront events. The completed
 * order boundary and Conversions API require a server/admin integration and do
 * not belong in a public theme asset.
 */
(function (window, document) {
  'use strict';

  var config = window.airjoOpenAIAdsConfig || {};
  var pixelId = config.pixelId;

  if (!pixelId || window.__airjoOpenAIAdsInitialized) {
    return;
  }

  window.__airjoOpenAIAdsInitialized = true;

  var consentState = 'unknown';
  var pendingEvents = [];
  var checkoutMeasured = false;
  var currency = config.currency;

  if (!window.oaiq) {
    var queue = function () {
      queue.q.push(arguments);
    };

    queue.q = [];
    window.oaiq = queue;

    var sdk = document.createElement('script');
    sdk.async = true;
    sdk.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

    var firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(sdk, firstScript);
    } else {
      document.head.appendChild(sdk);
    }
  }

  try {
    window.oaiq('consent', false);
    window.oaiq('init', { pixelId: pixelId });
  } catch (error) {
    return;
  }

  function send(eventName, data) {
    try {
      window.oaiq('measure', eventName, data);
    } catch (error) {
      // Measurement must never interrupt the storefront flow.
    }
  }

  function measure(eventName, data) {
    if (consentState === 'unknown') {
      pendingEvents.push({ eventName: eventName, data: data });
      return;
    }

    if (consentState === 'granted') {
      send(eventName, data);
    }
  }

  function setConsent(isAllowed) {
    consentState = isAllowed ? 'granted' : 'denied';

    try {
      window.oaiq('consent', Boolean(isAllowed));
    } catch (error) {
      // Keep consent synchronization non-blocking.
    }

    if (isAllowed) {
      pendingEvents.splice(0).forEach(function (event) {
        send(event.eventName, event.data);
      });
    } else {
      pendingEvents.length = 0;
    }
  }

  function syncShopifyConsent() {
    try {
      if (
        window.Shopify &&
        window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
      ) {
        setConsent(window.Shopify.customerPrivacy.analyticsProcessingAllowed());
        return;
      }
    } catch (error) {
      // Fall through to the privacy-safe default below.
    }

    setConsent(false);
  }

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1',
        onLoad: function (error) {
          if (error) {
            setConsent(false);
            return;
          }

          syncShopifyConsent();
        }
      }
    ]);
  } else {
    setConsent(false);
  }

  ['trackingConsentAccepted', 'trackingConsentDeclined', 'visitorConsentCollected'].forEach(function (eventName) {
    document.addEventListener(eventName, function () {
      window.setTimeout(syncShopifyConsent, 0);
    });
  });

  function positiveInteger(value, fallback) {
    var parsed = parseInt(value, 10);
    return parsed > 0 ? parsed : fallback;
  }

  function moneyInteger(value) {
    var parsed = Number(value);
    return isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null;
  }

  function normalizeContent(item, fallbackQuantity) {
    item = item || {};

    var id = item.variant_id || item.id;
    var name = item.product_title || item.title || item.name;
    var amount = moneyInteger(
      item.final_price !== undefined ? item.final_price :
      item.price !== undefined ? item.price :
      item.amount
    );
    var content = {
      content_type: 'product',
      quantity: positiveInteger(item.quantity, fallbackQuantity || 1)
    };

    if (id !== undefined && id !== null && String(id) !== '') {
      content.id = String(id);
    }

    if (name) {
      content.name = String(name);
    }

    if (amount !== null) {
      content.amount = amount;
      content.currency = currency;
    }

    return content;
  }

  function trackItemsAdded(items, fallbackQuantity) {
    var normalizedItems = (items || []).map(function (item) {
      return normalizeContent(item, fallbackQuantity);
    });
    var data = { type: 'contents' };

    if (normalizedItems.length) {
      data.contents = normalizedItems;
    }

    measure('items_added', data);
  }

  window.airjoOpenAIAds = {
    measure: measure,
    trackItemsAdded: trackItemsAdded
  };

  measure('page_viewed', { type: 'contents' });

  if (config.contentViewedData) {
    measure('contents_viewed', config.contentViewedData);
  }

  document.addEventListener('product:added', function (event) {
    var detail = event.detail || {};

    if (detail.productAdded === false || detail.openaiAdsMeasured === true) {
      return;
    }

    if (Array.isArray(detail.items) && detail.items.length) {
      trackItemsAdded(detail.items, detail.quantity);
      return;
    }

    if (detail.variant) {
      trackItemsAdded([detail.variant], detail.quantity);
      return;
    }

    var form = detail.button && detail.button.closest ? detail.button.closest('form[action*="/cart/add"]') : null;
    var variantInput = form ? form.querySelector('[name="id"]') : null;

    trackItemsAdded(
      variantInput ? [{ id: variantInput.value, quantity: detail.quantity }] : [],
      detail.quantity
    );
  });

  document.addEventListener('openai-ads:items-added', function (event) {
    var detail = event.detail || {};

    if (detail.productAdded === false) {
      return;
    }

    trackItemsAdded(detail.items || [], detail.quantity);
  });

  function trackCheckoutStarted() {
    if (checkoutMeasured) {
      return;
    }

    checkoutMeasured = true;
    measure('checkout_started', config.checkoutStartedData || { type: 'contents' });
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target.closest(
      '[name="checkout"], a[href="/checkout"], a[href^="/checkout?"], .shopify-payment-button__button'
    ) : null;

    if (target) {
      trackCheckoutStarted();
    }
  }, true);
})(window, document);
